import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { EventsPage } from './pages/EventsPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { DynamicPage } from './pages/DynamicPage';

const builtInPages: string[] = ['home', 'events', 'about', 'contact'];

interface Organization {
  id: string;
  name: string;
  logoUrl: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  website: string | null;
}

function App() {
  const [currentPage, setCurrentPage] = useState<string>('home');
  const orgSlug = import.meta.env.VITE_ORG_SLUG || 'demo-synagogue';

  const { data: org } = useQuery({
    queryKey: ['organization', orgSlug],
    queryFn: async () => {
      const response = await api.get<Organization>(`/organizations/by-slug/${orgSlug}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Simple hash-based routing with dynamic page support
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'home';
      setCurrentPage(hash);
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (page: string) => {
    window.location.hash = page === 'home' ? '' : page;
    setCurrentPage(page);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage org={org} orgSlug={orgSlug} onNavigate={navigate} />;
      case 'events':
        return <EventsPage orgSlug={orgSlug} />;
      case 'about':
        return <AboutPage org={org} />;
      case 'contact':
        return <ContactPage org={org} />;
      default:
        // Dynamic page: fetch from API by slug
        return <DynamicPage slug={currentPage} />;
    }
  };

  const isBuiltIn = builtInPages.includes(currentPage);

  return (
    <Layout
      org={org}
      currentPage={currentPage}
      onNavigate={navigate}
      isBuiltInPage={isBuiltIn}
    >
      {renderPage()}
    </Layout>
  );
}

export default App;
